// v0.8.4 — 课表时段预留 (辉哥 10/6 拍板)
//
// ─── v0.15.0 (辉哥 10/10 拍板 A 方案) 大幅瘦身: 只剩「冲突判定」一件事 ──────────────
//
// 【为什么还留着这个文件】
//   预留块的**渲染和交互**整体迁到 FullCalendar event 了 (见 CalendarTab.js 的
//   buildReservationEventDefs / buildReservationContentNodes / handleReservationResize 等),
//   这里原来 690 行里的绝大部分随之失效, 已整体删除:
//     · computeSlotPixelHeight()   —— 从 .fc-timegrid-slot-lane 反推格高像素
//     · getFirstSlotInfo()         —— 从 lane data-time 反推当天第一格的 ms
//     · _renderOne() / render()    —— 绝对定位 div + 四角手柄 DOM + 占格徽标
//     · _onHandleAreaPointerDown() —— 四角 pointerdown / 按格吸附 / ghost / snap line /
//                                     cell 高亮 / dayCap / dirFlag 的全部逻辑
//     · _ensureGhost / _ensureSnapLine / _ensureCellHi / _ensureDayCap / _ensureDirFlag /
//       _destroyOverlays           —— 那些 overlay 工厂
//     · setReservations()          —— 喂数据 + 触发重绘的入口 (FC event 由 tryMountCalendar 建源)
//
// 【迁移原因 (CHANGELOG v0.15.0 有完整版)】
//   ① 老实现依赖 FC **私有 DOM** (.fc-timegrid-col-frame / .fc-timegrid-slot-lane[data-time]),
//      FC 一改内部结构就断, 而且断了不报错 —— 只是块悄悄画错位置。
//   ② getFirstSlotInfo() 用 new Date() (真实今天) 当基准, 而渲染过滤用 calendarTab.currentDate,
//      翻到**非今天**的日期时预留块整体偏出屏外 (本地实测 top=-7000px)。
//      改 FC event 后位置由 FC 自己算, 这段代码删掉, bug 随之消失。
//
// 【保留下来的三样东西, 各自仍有真实调用方】
//   · _checkConflict()  — CalendarTab 的 eventAllow / 落库前兜底都调它 (冲突判定逻辑不重写一份)
//   · destroy()         — CalendarTab.onremove 调它
//   · 构造函数里的 calendarTab 引用 — _checkConflict 要读 calendarTab.lessons
//
// 【一条设计上的变化, 请注意】
//   _checkConflict() 第二段 (「别的预留是否重叠」) 原先读 `this.reservations`,
//   也就是本类自己那份由 setReservations() 灌进来的副本。现在 setReservations() 没了,
//   改成直接读 `this.calendarTab.slotReservations` ——
//   **单一事实源**: 课表组件那份数组本来就是唯一被 load() 刷新的副本, 两处各存一份必然漂移。
//
// 【视觉保持的做法】
//   内容 DOM 结构 (.zs-rsv-in / .zs-rsv-title / .zs-rsv-why / .zs-rsv-gran) 一个字没改,
//   LESS 段也一行没改 —— 迁移后的黄斜纹、虚线框、圆角、「占 N 格」徽标全部沿用同一批 class。
//   构造在 CalendarTab.buildReservationContentNodes() 里。

// 'YYYY-MM-DD HH:MM:SS' → Date (与 CalendarTab 的 zsParseLocal 同语义, 见那边顶部注释)
function parseLocal(dtStr) {
  if (!dtStr) return null;
  return new Date(dtStr.replace(' ', 'T'));
}
export default class SlotReservationRenderer {
  constructor(opts) {
    // opts: { calendarTab }
    this.calendarTab = opts.calendarTab;
  }

  /**
   * 目标教室的 [startMs, endMs) 撞没撞东西?
   *   ① 撞约课 (calendarTab.lessons)
   *   ② 撞别的预留 (calendarTab.slotReservations, 排除自己那条)
   *
   * ⚠️ 调用方 (CalendarTab.eventAllow / persistReservationMove) 拿到 false 时一律
   *   「弹回原位 + 不落库」—— 这是辉哥 10/10 拍板的新行为。
   *   老行为是「ghost 转红 + 按最后合法范围保存」(v0.8.4 决策 ⑨), 随自绘代码一起删了。
   *
   * @param {number} roomId        目标教室原生 id (不是 'r_<id>')
   * @param {number} startMs       起始时刻 ms
   * @param {number} endMs         结束时刻 ms
   * @param {?number} excludeRsvId 排除的那条预留 id (拖/调自己时要排除)
   */
  _checkConflict(roomId, startMs, endMs, excludeRsvId) {
    const cal = this.calendarTab;
    // ① 撞约课
    const lessons = cal && cal.lessons || [];
    for (const l of lessons) {
      if (l.room_id !== roomId) continue;
      const lStart = parseLocal(l.start_at);
      const lEnd = parseLocal(l.end_at);
      if (!lStart || !lEnd) continue;
      // 半开区间相交: lStart < endMs && lEnd > startMs
      if (lStart.getTime() < endMs && lEnd.getTime() > startMs) {
        return true;
      }
    }
    // ② 撞别的预留 (避免两条预留叠在一起)
    const list = cal && cal.slotReservations || [];
    for (const r of list) {
      if (!r || !r.start_at || !r.end_at) continue;
      if (excludeRsvId != null && String(r.id) === String(excludeRsvId)) continue;
      if (r.room_id !== roomId) continue;
      const rStart = parseLocal(r.start_at);
      const rEnd = parseLocal(r.end_at);
      if (!rStart || !rEnd) continue;
      if (rStart.getTime() < endMs && rEnd.getTime() > startMs) {
        return true;
      }
    }
    return false;
  }

  /** v0.15.0 — 自绘 overlay 全部删除后这里没有东西要清, 方法保留只为调用方不用改 */
  destroy() {/* noop */}
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/SlotReservationRenderer', SlotReservationRenderer);